<template>
  <config-base class="zcm-storage-config">
    <template #config>
      <div>
        <zm-radio-group
          v-model="selectedPlanCode"
          enter-select
          aria-label="Select a capacity"
          class="opc-card-radio zcm-storage-config__radio-group"
          @change="handlePlanCodeChange"
        >
          <zm-radio
            data-testid="checkout-radio-94"
            v-for="plan in planList"
            :key="plan.plan_code"
            :label="plan.plan_code"
            :disabled="calculateIsOptionDisabled(plan)"
          >
            <p class="zcm-storage-config__radio-group__capacity">{{ plan.cartCapacity }} {{ plan.cartCapacityUnit }}</p>
            <div>
              <p class="zcm-storage-config__radio-group__price">
                {{ $money(plan.amount, { forceDiff: true }) }}
              </p>
              <span class="zcm-storage-config__radio-group__cycle">
                <span v-html="getMergedBilledText(plan.billing_cycle, plan.amount, planUnitMap.credits)"></span>
                <priceVATTip />
              </span>
            </div>
          </zm-radio>
        </zm-radio-group>
        <preview-payment class="gap24" />
      </div>
    </template>
    <template #footer v-if="isShowRemoveBtn">
      <zm-button
        data-testid="checkout-button-95"
        type="link"
        ghost
        @click="removePlanByKey"
        :aria-label="`remove ${title} plan`"
      >
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script lang="jsx">
import { mapGetters, mapMutations, mapActions } from 'vuex';
import ConfigBase from '@/components/config-base.vue';
import PreviewPayment from '@/views/checkout/payment/preview-payment.vue';
import { plansof } from '@/utils/chain';
import { translateToEn } from '@/plugins/i18n';
import plansMixin from '../mx/plan';
import commonMixin from './mixin';
import { checkMRSChange } from '@/utils/validator';
import { MRS_STATUS_TYPE_MAP } from '@/common/constants';
import { getMergedBilledText } from '@/common/product-unit';
import { planUnitMap } from '@/common/product-unit';

const ConfigBaseComp = {
  name: 'ZCMStorageBase',
  extends: ConfigBase,
  mixins: [commonMixin],
  data() {
    return {
      showLearnMore: false,
      showPlanDetails: false,
    };
  },
  methods: {
    /**
     * override ConfigBase
     * @param {object} plan
     */
    mx_renderCapacity(plan) {
      if (!plan?.capacity) return '';
      return `${plan?.cartCapacity} ${plan?.cartCapacityUnit}`;
    },

    /**
     * override ConfigBase
     */
    mx_renderLicense(plan) {
      return this.$money(plan.amount, { forceDiff: true });
    },

    /**
     * override ConfigBase
     * @param {object} plan
     */
    // mx_renderCycle(plan) {
    //   return (
    //     <li>
    //       {this.$money(plan.amount, { forceDiff: true })}
    //       {this.$t(`billing.common.per_billing_cycle.${plan.billing_cycle}`)}
    //     </li>
    //   );
    // },
  },
};

export default {
  name: 'ZCMStorageConfig',
  mixins: [commonMixin, plansMixin],
  components: { ConfigBase: ConfigBaseComp, PreviewPayment },
  data() {
    return {
      selectedPlanCode: '',
      planUnitMap: Object.freeze(planUnitMap),
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'planMapFlat', 'currentEditPlan']),

    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },

    planList() {
      return this.planMapFlat.filter((p) => p.type === this.planType);
    },

    planTrackingName() {
      return translateToEn(this.selectedPlan?.planNameKey);
    },
    disableDownSell() {
      return this.basicConfig.downSellData.disableDownSell;
    },
  },
  watch: {
    // update selected plan
    selectedPlanCode(newCode, oldCode) {
      if (oldCode) {
        this.switchSelectPlan([oldCode, newCode, undefined]);
      } else {
        this.updateSelectPlan({
          plan_code: newCode,
          cartInfo: { selected_number: 1 },
        });
      }
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchSelectPlan']),
    getMergedBilledText,
    /**
     * plan code changed
     * @param {string} planCode
     */
    handlePlanCodeChange(planCode) {
      const plan = this.planList.find((plan) => plan.plan_code === planCode);
      if (plan) {
        this.$tracking.buyInteraction(`${this.planTrackingName} plan`, `${plan.cartCapacity}${plan.cartCapacityUnit}`);
      }
    },
    calculateIsOptionDisabled(plan) {
      if (this.disableDownSell) {
        return (
          checkMRSChange({
            oldPlan: this.currentEditPlan,
            nextPlan: plan,
            nextPlanQuantity: 1,
          }) === MRS_STATUS_TYPE_MAP.CHANGE_FUTURE
        );
      }
      return false;
    },
  },

  created() {
    if (this.selectedPlan) {
      this.selectedPlanCode = this.selectedPlan.plan_code;
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/zoom-card.scss';
.gap24 {
  margin-top: 24px;
}

.zcm-storage-config {
  :deep .opc-zm-base-card {
    &__name {
      text-transform: none;
      padding-right: 50px;
    }
    &__main--desc__content {
      font-size: 14px;
      color: var(--zoom-color-text-stronger-neutral, #2a2b2d);
    }
  }

  &__radio-group {
    flex-wrap: wrap;
    :deep .zbo-radio {
      flex: 0 0 calc(50% - 8px);
      @include media(mobile) {
        width: 100%;
      }
      &__wrap {
        width: 100%;
      }

      &__label {
        display: flex;
        flex-direction: column;
      }
    }
    &__capacity {
      color: var(--text-text-stronger-neutral, #2A2B2D);
      /* title-3 */
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px; /* 125% */
      letter-spacing: -0.31px;
    }
    &__price {
      color: var(--zoom-color-text-stronger-neutral, #2a2b2d);
      font-size: 24px;
      font-style: normal;
      font-weight: 590;
      line-height: 28px; /* 116.667% */
      letter-spacing: 0.07px;
      margin: 4px 0px 2px;
    }
    &__cycle {
      display: flex;
      font-weight: 400;
      color: var(--zoom-color-text-stronger-neutral, #2a2b2d);
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: -0.15px;
    }
  }
}
</style>
